import Marionette from "backbone.marionette";
import Radio from "backbone.radio";

import {
  errorParser,
  dateFormatter,
  INPUT_DATE_FORMAT,
  OUTPUT_DATE_FORMAT,
} from "../../../../utils";

import ChosenBehavior from "../../../../behaviors/chosen";
import DatePickerBehavior from "../../../../behaviors/kendo-ui/datepicker";
import TooltipBehavior from "../../../../behaviors/tooltip";

import templates from "../../../../utils/templates";
import simpleAlert from "../../../../utils/alerts/simple";

const DEFAULTS = {
  tagName: "form",
  template: templates.find("chart_reviews", "chart_edit_chart_form"),
};

const channel = Radio.channel("global");

class View extends Marionette.ItemView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  behaviors() {
    return [
      { behaviorClass: ChosenBehavior },
      { behaviorClass: DatePickerBehavior },
      { behaviorClass: TooltipBehavior },
    ];
  }

  ui() {
    return {
      // dropdowns
      statusValue: "#statusValue",
      projectId: "#projectId",
      retrievalStatus: "#retrievalStatus",
      retrievalStatusNAReason: "#retrievalStatusNAReason",
      // text inputs
      receivedDate: "#receivedDate",
      customerLocation: "#customerLocation",
      providerGroupNumber: "#providerGroupNumber",
      customerRefNumber: "#customerRefNumber",
      userField1: "#userField1",
      userField2: "#userField2",
      userField3: "#userField3",
      // multi dropdowns
      reviewers: "#reviewers",
      chartAttributes: "#chartAttributes",
    };
  }

  events() {
    return {
      "change @ui.statusValue": "_handleTextInputDropdown",
      "change @ui.projectId": "_handleSingleNumericDropdown",
      "change @ui.retrievalStatus": "_handleTextInputDropdown",
      "change @ui.retrievalStatusNAReason": "_handleTextInputDropdown",
      // text inputs
      "change @ui.receivedDate": "_onDateReceivedChange",
      "change @ui.customerLocation": "_handleTextInput",
      "change @ui.customerRefNumber": "_handleTextInput",
      "change @ui.userField1": "_handleTextInput",
      "change @ui.userField2": "_handleTextInput",
      "change @ui.userField3": "_handleTextInput",
      "keyup @ui.providerGroupNumber": "_uppercase",
      "change @ui.providerGroupNumber": "_handleTextInput",
      // multi dropdowns
      "change @ui.reviewers": "_handleMultipleNumericDropdown",
      "change @ui.chartAttributes": "_handleMultipleNumericDropdown",
      // buttons
      reset: "_onReset",
      submit: _.debounce(this._onSubmit, 200),
    };
  }

  initialize() {
    this.apiModel = this.options.apiModel;
    this.apiOriginalData = this.apiModel.toJSON();

    // listen for changes
    this.listenTo(this.apiModel, "change", this._onAPIModelChange);

    // make sure to render the view when the model changes
    this.listenTo(this.model, "change:statusValue", this._onStatusChange);
    this.listenTo(this.model, "change:projectId", this._onProjectChange);
    this.listenTo(
      this.model,
      "change:retrievalStatus",
      this._onRetrievalStatusChange
    );
    this.listenTo(this.model, "change:reviewers", this._onReviewersChange);
    this.listenTo(
      this.model,
      "change:chartAttributes",
      this._onChartAttributesChange
    );
    this.listenTo(this.model, "change", this._onUIModelChange);
  }

  onAttach() {
    if (this.options.active) {
      this._notifyBehaviors();
    }
  }

  // trims plain text input fields
  _handleTextInput(e) {
    const $el = $(e.target);
    const value = $el.val().trim();
    $el.val(value);

    // update property on model
    this.apiModel.set($el[0].id, value);
  }

  _handleTextInputDropdown(e) {
    const $el = $(e.target);
    const name = $el[0].id;
    const selectedOptions = $(":selected", $el);
    const selected = selectedOptions.map((index, option) => option.value);
    const value =
      selected.length > 1 ? selected.join(",") : selected.get(0) || null;

    try {
      const listName = `${name}List`;
      this.model.get(listName).map((item) => {
        const match = value === item.getKey();
        item.setChecked(match);
        return item;
      });
    } catch (err) {
      // do nothing
    }

    // update property on model
    this.apiModel.set(name, value);
  }

  _onDateReceivedChange() {
    const field = this.ui.receivedDate;
    const value = field.val().trim();

    let receivedDate = null;
    if (value !== "") {
      receivedDate = dateFormatter(
        value,
        OUTPUT_DATE_FORMAT,
        INPUT_DATE_FORMAT
      );
    }

    // update UI field
    field.val(receivedDate);

    // update model
    this.apiModel.set({
      receivedDate,
    });
  }

  _handleSingleNumericDropdown(e) {
    const $el = $(e.target);
    const selectedOptions = $(":selected", $el);
    const selected = selectedOptions.map((index, option) => +option.value);
    const value = selected.length > 1 ? selected : selected.get(0) || null;

    // update property on model
    this.apiModel.set($el[0].id, value);
  }

  _handleMultipleNumericDropdown(e) {
    const $el = $(e.target);
    const selectedOptions = $(":selected", $el);
    const selected =
      selectedOptions.map((index, option) => +option.value) || [];

    // update property on model
    this.apiModel.set($el[0].id, selected);
  }

  _uppercase(e) {
    const $el = $(e.target);
    const text = $el.val().trim().toUpperCase();
    $el.val(text);
  }

  _onStatusChange(model, key) {
    const coll = model.getStatusList();
    coll.map((item) => item.setChecked(false));
    const active = coll.findWhere({ key });
    active && active.setChecked(true);
    model.setStatusList(coll.toJSON());
  }

  _onProjectChange(model, key) {
    const coll = model.getProjectIdList();
    coll.map((item) => item.setChecked(false));
    const active = coll.findWhere({ key });
    active && active.setChecked(true);
    model.setProjectIdList(coll.toJSON());
  }

  _onRetrievalStatusChange(model, key) {
    const coll = model.getRetrievalStatusList();
    coll.map((item) => item.setChecked(false));
    const active = coll.findWhere({ key });
    active && active.setChecked(true);
    model.setRetrievalStatusList(coll.toJSON());
  }

  _onReviewersChange(model, list) {
    const coll = model.getReviewersList();
    coll.map((item) => item.setChecked(false));
    list.forEach((id) => {
      const active = coll.findWhere({ id });
      active && active.setChecked(true);
    });
    model.setReviewersList(coll.toJSON());
  }

  _onChartAttributesChange(model, list) {
    const coll = model.getChartAttributeList();
    coll.map((item) => item.setChecked(false));
    list.forEach((key) => {
      const active = coll.findWhere({ key });
      active && active.setChecked(true);
    });
    model.setChartAttributeList(coll.toJSON());
  }

  _onAPIModelChange(model) {
    // update UI model with API model changes
    this.model.set(model.changed);
  }

  _onUIModelChange() {
    this.render();
    this._notifyBehaviors();
  }

  _notifyBehaviors() {
    this.triggerMethod("ContentRendered");
  }

  _onReset() {
    // reset api model with original data
    this.apiModel.set(this.apiOriginalData);

    // notify Chosen behavior the form was reset
    this.triggerMethod("Reset");
  }

  _onSubmit(e) {
    e.preventDefault();

    // validate it
    if (this.apiModel.isValid()) {
      // attempt to update chart
      this.apiModel.save(this.apiModel.attrs, {
        success: this._onSync.bind(this),
        error: this._onSyncFailure.bind(this),
      });
    } else {
      // show warning message
      channel.trigger("status:show", {
        type: "warning",
        message: this.apiModel.validationError,
      });
    }
  }

  _onSync() {
    // show success message
    channel.trigger("status:show", {
      type: "success",
      message: "Your chart was updated successfully.",
    });

    // notify parent layout of this change
    this.triggerMethod("modal:close");
  }

  _onSyncFailure(model, response) {
    let message = errorParser(response);
    try {
      message = response.responseJSON.error["error-message"];
    } catch (e) {
      console.warn("Unable to find additional info on thrown exception.");
    }

    // show error
    simpleAlert({
      type: "error",
      title: "Your changes could not be saved.",
      text: message,
    });
  }
}

export { View as default };
